import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactPlayer from "react-player/youtube";
import Navbar from "./Navbar/Navbar";
import LikeButtons from "./LikeButtons";
import StarRating from "./StarRating";
import { GiBlackFlag } from "react-icons/gi";
import { SiApplemusic } from "react-icons/si";
import { useParams } from "react-router-dom";
import Particle from "./Particle";
import BanderaMedia from "./BanderaMedia";

import {
  FaTiktok,
  FaWhatsapp,
  FaInstagramSquare,
  FaYoutube,
  FaShare,
  FaSpotify,
  FaFacebook,
  FaWikipediaW,
} from "react-icons/fa";

import { IoTicketSharp } from "react-icons/io5";
import { BsTwitterX } from "react-icons/bs";
import { loadCardData } from "../utils/loadCardData";
import "./banddetail.css";

// Error boundary for ReactPlayer
class PlayerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.warn("ReactPlayer error caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ color: "white" }}>Video failed to load.</div>;
    }
    return this.props.children;
  }
}

const socialMediaLinks = [
  { href: (band) => band.websiteurl, icon: <GiBlackFlag />, className: "icons-bandera" },
  { href: (band) => band.facebook, icon: <FaFacebook />, className: "icons-fb" },
  { href: (band) => band.youtube, icon: <FaYoutube />, className: "icons-YT" },
  { href: (band) => band.instagram, icon: <FaInstagramSquare />, className: "icons-instagram" },
  { href: (band) => band.twitter, icon: <BsTwitterX />, className: "icons-twitter" },
  { href: (band) => band.tiktok, icon: <FaTiktok />, className: "icons-tiktok" },
  { href: (band) => band.ticketmaster, icon: <IoTicketSharp />, className: "icons-ticketmaster" },
  { href: (band) => band.wiki, icon: <FaWikipediaW />, className: "icons-wiki" },
  { href: (band) => band.spotify, icon: <FaSpotify />, className: "icons-spotify" },
  { href: (band) => band.apple, icon: <SiApplemusic />, className: "icons-itunes" },
];

const isYouTubePlaylist = (url) => {
  try {
    const u = new URL(url);
    return u.hostname.includes("youtube.com") && u.searchParams.has("list");
  } catch {
    return false;
  }
};

// Privacy-enhanced YouTube embed
const toPlaylistEmbed = (url) => {
  const playlistId = new URL(url).searchParams.get("list");
  return `https://www.youtube-nocookie.com/embed/videoseries?list=${playlistId}`;
};

const BandDetail = () => {
  const [band, setBand] = useState(null);
  const [loading, setLoading] = useState(true);
  const { title } = useParams();
  const videoContainerRef = useRef(null); // ref for passive scroll

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bandsData = await loadCardData();
        const foundBand = bandsData.find((item) => item.title === title);
        setBand(foundBand);
      } catch (error) {
        console.error("Error loading bands data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [title]);

  // Attach passive touch listener
  useEffect(() => {
    const container = videoContainerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      // No preventDefault(), passive = true
    };
    container.addEventListener("touchstart", handleTouchStart, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
    };
  }, []);

  const playerKey = useMemo(() => band?.videourl ?? "", [band?.videourl]);

  if (loading) return <div>Loading...</div>;
  if (!band) return <div>Band not found</div>;

  const encodedTitle = encodeURIComponent(band.title);
  const shareUrl = `${window.location.origin}/Bandera/${encodedTitle}`;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: document.title,
        text: `Te Invitamos a disfrutar de ${band.title} con Bandera Musical.`,
        url: shareUrl,
      }).catch((error) => console.error("Error sharing:", error));
    } else {
      console.warn("Web Share API not supported");
    }
  };

  return (
    <div>
      <Particle id="particles" />
      <Navbar />

      <div className="band-card">
        <div className="video-container" ref={videoContainerRef}>
          {isYouTubePlaylist(band.videourl) ? (
            <iframe
              className="video"
              width="100%"
              height="100%"
              src={toPlaylistEmbed(band.videourl)}
              title="YouTube Playlist"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          ) : (
            <PlayerErrorBoundary>
              <ReactPlayer
                key={playerKey}
                url={band.videourl}
                className="video"
                width="100%"
                height="100%"
                playing={false}
                controls
                muted={false}
                onError={(e) => console.warn("Player error:", e)}
                onReady={() => console.log("Player ready")}
                config={{
                  youtube: {
                    playerVars: {
                      modestbranding: 1,
                      rel: 0,
                      disablekb: 1,
                      origin: window.location.origin,
                    },
                  },
                }}
              />
            </PlayerErrorBoundary>
          )}
        </div>

        <div className="title-name">
          <h2 style={{ color: "white" }}>{band.title}</h2>
        </div>

        <div className="card--titler">
          <p>{band.category}</p>
        </div>

        <div>
          <img src={`../images/${band.locationImg}`} className="origin-flag" alt="location" />
        </div>

        <StarRating />
        <LikeButtons />

        <p
          className="description"
          style={{
            backgroundColor: "black",
            fontFamily: "Iceland",
            fontSize: "1rem",
            borderRadius: "10px",
          }}
        >
          {band.description}
        </p>

        <div className="sharebutton">
          <button className="sharebutton-button" onClick={handleShare} role="link">
            <FaShare />
          </button>

          <a
            className="sharebutton-button"
            href={`whatsapp://send?text=${encodeURIComponent(
              `Te Invitamos a disfrutar de ${band.title} con Bandera Musical. ${shareUrl}`
            )}`}
          >
            <FaWhatsapp className="sharebutton-button-2" />
          </a>
        </div>

        <div className="slidertextdiv">
          <h1 className="slidertext">
            Presiona un Link para visitar la red social del artista
          </h1>
        </div>

        <div className="third-party-icons">
          {socialMediaLinks.map((link, index) => {
            const href = link.href(band);
            if (!href) return null;
            return (
              <a
                key={index}
                target="_blank"
                rel="noreferrer"
                href={href}
                className={`icons ${link.className}`}
                id="third-party-grid"
              >
                {link.icon}
              </a>
            );
          })}
        </div>

        <BanderaMedia style={{ color: "goldenrod" }} />
      </div>
    </div>
  );
};

export default BandDetail;